/**
 * Events API Service
 * Handles all API calls for event calendar management
 * NOTE: This follows the OpenAPI Generator pattern with Redux middleware
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../../../openapi/ccdb-bff/types';
import type {CcdbError} from '../../../openapi/ccdb-bff/models/CcdbError';
import {ROOT} from '../../../middleware/api';

export interface EventSearchParams {
    searchText?: string;
    searchType?: string;
    searchField?: string;
    eventType?: string;
    eventGroup?: string;
    coverage?: string;
    eventDateStatus?: string;
    status?: string;
    dateType?: string;
    startDate?: string;
    endDate?: string;
    missingInfo?: boolean;
    pageSize: number;
    currentPage: number;
}

export interface EventData {
    eventId: number;
    event: string;
    companyId: string;
    companyName: string;
    eventYear: string;
    eventType: string;
    beginingDate: string; // Note: API has typo "begining" instead of "beginning"
    endDate: string;
    time: string;
    processPriority: number;
    statusName: string;
    status: number;
    modifiedBy: string;
    companyIndex: string;
    modificationDate: string;
    creationDate: string;
    eventGroupName: string;
    ffPriority: string | null;
    eventDetailUrl: string | null;
    webcastUrl: string;
    webcastPhone: string;
    timeZoneId: number;
    dialInDetails: string;
    fiscalYearEndDate: string;
    isRepeat: boolean;
    imagePath: string | null;
    eventDateStatus: string | null;
    row: number;
}

export interface EventSearchResponse {
    events: EventData[];
    totalRecords: number;
    pageSize: number;
    currentPage: number;
}

// ============================================
// Search Events
// ============================================

export interface SearchEvents$Request extends EventSearchParams {}

export interface SearchEvents$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const searchEventsRequest = createAction<PayloadData<void, SearchEvents$Request>>('CCDB_BFF__SEARCH_EVENTS__REQUEST');
export const searchEventsSuccess = createAction<PayloadData<EventSearchResponse, SearchEvents$Request>>('CCDB_BFF__SEARCH_EVENTS__SUCCESS');
export const searchEventsFailure = createAction<PayloadData<CcdbError, SearchEvents$Request, true>>('CCDB_BFF__SEARCH_EVENTS__FAILURE');

export const searchEventsActions = [
    searchEventsRequest,
    searchEventsSuccess,
    searchEventsFailure,
] as const;

/**
 * Search events with pagination and filters
 * GET /api/events/search?{filters}
 */
export const searchEvents = (meta: SearchEvents$Request, config?: SearchEvents$Config) => {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    // Add optional parameters only if they have values
    if (meta.searchText) queryParams.append('searchText', meta.searchText);
    if (meta.searchType) queryParams.append('searchType', meta.searchType);
    if (meta.searchField) queryParams.append('searchField', meta.searchField);
    if (meta.eventType) queryParams.append('eventType', meta.eventType);
    if (meta.eventGroup) queryParams.append('eventGroup', meta.eventGroup);
    if (meta.coverage) queryParams.append('coverage', meta.coverage);
    if (meta.eventDateStatus) queryParams.append('eventDateStatus', meta.eventDateStatus);
    if (meta.status) queryParams.append('status', meta.status);
    if (meta.dateType) queryParams.append('dateType', meta.dateType);
    if (meta.startDate) queryParams.append('startDate', meta.startDate);
    if (meta.endDate) queryParams.append('endDate', meta.endDate);
    if (meta.missingInfo) queryParams.append('isMissingInfo', 'true');
    
    // Always add pagination parameters
    queryParams.append('pageSize', meta.pageSize.toString());
    queryParams.append('currentPage', meta.currentPage.toString());
    
    const endpoint = `/api/events/search?${queryParams}`;
    
    return {
        CALL_API: {
            types: searchEventsActions,
            method: ApiMethods.GET,
            endpoint,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...meta,
    };
};

/**
 * Get event by ID
 */
export const getEventById = async (eventId: string): Promise<EventData> => {
    const response = await fetch(`${ROOT}/api/events/${encodeURIComponent(eventId)}`);

    if (!response.ok) {
        const error = await response.json().catch(() => ({
            code: response.status,
            message: `Failed to fetch event (HTTP ${response.status})`,
        }));
        throw error;
    }

    return response.json();
};

/**
 * Create new event
 */
export const createEvent = async (event: Omit<EventData, 'index'>): Promise<EventData> => {
    const response = await fetch(`${ROOT}/api/events`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({
            code: response.status,
            message: `Failed to create event (HTTP ${response.status})`,
        }));
        throw error;
    }

    return response.json();
};

/**
 * Update event
 */
export const updateEvent = async (eventId: string, event: Partial<EventData>): Promise<EventData> => {
    const response = await fetch(`${ROOT}/api/events/${encodeURIComponent(eventId)}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({
            code: response.status,
            message: `Failed to update event (HTTP ${response.status})`,
        }));
        throw error;
    }

    return response.json();
};

/**
 * Delete event
 */
export const deleteEvent = async (eventId: string): Promise<void> => {
    const response = await fetch(`${ROOT}/api/events/${encodeURIComponent(eventId)}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({
            code: response.status,
            message: `Failed to delete event (HTTP ${response.status})`,
        }));
        throw error;
    }
};

// ============================================
// Toggle Event Status
// ============================================

/** Normalized target status values sent to the API */
export type ToggleTargetStatus = 'ACTIVE' | 'INACTIVE';

export interface ToggleStatusResult {
    eventId: number;
    success: boolean;
    currentStatus: string;
    reason: string;
}

export interface ToggleStatusResponse {
    responseCode: number;
    message: string;
    objectData: ToggleStatusResult[];
}

/**
 * Toggle event status (active / inactive)
 * PUT /api/events/toggle-status
 * targetStatus is case-insensitive; normalised to uppercase before sending.
 */
export const toggleEventStatus = async (
    eventIds: number[],
    targetStatus: string,
): Promise<ToggleStatusResponse> => {
    const normalizedStatus = targetStatus.toUpperCase() as ToggleTargetStatus;

    const response = await fetch(`${ROOT}/api/events/toggle-status`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        body: JSON.stringify({eventIds, targetStatus: normalizedStatus}),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({
            code: response.status,
            message: `Failed to toggle event status (HTTP ${response.status})`,
        }));
        throw error;
    }

    return response.json();
};
